import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import type { Group } from "three";

interface TreeSproutProps {
  position: [number, number, number];
  hue?: number;
  /** Called once the sprout animation settles (~2.5s). */
  onSettled?: () => void;
}

/**
 * Animated tree sprout: trunk grows up from the ground, then foliage clusters
 * pop into existence one by one from scale 0. The whole sequence is ~2.5s.
 */
export function TreeSprout({
  position,
  hue = 110,
  onSettled,
}: TreeSproutProps) {
  const groupRef = useRef<Group>(null);
  const trunkRef = useRef<Group>(null);
  const canopyRef = useRef<Group>(null);
  const startTime = useRef<number | null>(null);
  const settled = useRef(false);

  const TRUNK_HEIGHT = 3.2;
  const TRUNK_RADIUS = 0.12;
  const canopyColor = `hsl(${hue}, 82%, 55%)`;
  const trunkColor = "#6b5a45";

  const foliageClusters = useMemo(() => {
    const clusters: { x: number; y: number; z: number; r: number }[] = [];
    const crownY = TRUNK_HEIGHT * 0.78;
    const baseR = 1.0;

    clusters.push({ x: 0, y: crownY + baseR * 0.35, z: 0, r: baseR });
    clusters.push({ x: 0, y: crownY + baseR * 0.95, z: 0, r: baseR * 0.55 });

    const count = 7;
    for (let i = 0; i < count; i++) {
      const a = (i / count) * Math.PI * 2;
      const spread = baseR * 0.7;
      const yOff = ((i * 13) % 7 - 3) * 0.1;
      const sz = baseR * (0.35 + ((i * 11) % 5) * 0.07);
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY + yOff,
        z: Math.sin(a) * spread,
        r: sz,
      });
    }

    const lowerCount = 4;
    for (let i = 0; i < lowerCount; i++) {
      const a = (i / lowerCount) * Math.PI * 2 + 0.3;
      const spread = baseR * 0.85 + 0.25;
      clusters.push({
        x: Math.cos(a) * spread,
        y: crownY - 0.35 - ((i * 17) % 4) * 0.12,
        z: Math.sin(a) * spread,
        r: baseR * 0.3,
      });
    }
    return clusters;
  }, []);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;

    const trunk = trunkRef.current;
    const canopy = canopyRef.current;
    if (!trunk || !canopy) return;

    if (t < 0.8) {
      // Phase 1: trunk grows up from ground
      const p = t / 0.8;
      const eased = 1 - Math.pow(1 - p, 3);
      trunk.scale.set(1, eased, 1);
      trunk.position.y = (TRUNK_HEIGHT / 2) * eased;
      canopy.scale.setScalar(0);
    } else if (t < 2.0) {
      // Phase 2: foliage pops in sequentially
      trunk.scale.set(1, 1, 1);
      trunk.position.y = TRUNK_HEIGHT / 2;

      const foliageP = (t - 0.8) / 1.2;
      const children = canopy.children;
      for (let i = 0; i < children.length; i++) {
        const clusterStart = i / children.length;
        const localP = Math.max(0, (foliageP - clusterStart) / 0.3);
        const clamped = Math.min(1, localP);
        const bounce =
          clamped < 1
            ? 1 - Math.pow(1 - clamped, 3)
            : 1 + Math.sin(clamped * Math.PI) * 0.1;
        const s = Math.min(bounce, 1.15);
        children[i].scale.setScalar(s);
      }
    } else {
      // Settled — gentle sway like a normal tree
      trunk.scale.set(1, 1, 1);
      trunk.position.y = TRUNK_HEIGHT / 2;
      const children = canopy.children;
      for (let i = 0; i < children.length; i++) {
        children[i].scale.setScalar(1);
      }

      const sway = Math.sin(clock.elapsedTime * 0.6) * 0.04;
      if (groupRef.current) groupRef.current.rotation.z = sway;

      if (!settled.current) {
        settled.current = true;
        onSettled?.();
      }
    }
  });

  return (
    <group ref={groupRef} position={position}>
      <group ref={trunkRef} position={[0, 0, 0]} scale={[1, 0, 1]}>
        <mesh>
          <cylinderGeometry
            args={[TRUNK_RADIUS * 0.65, TRUNK_RADIUS, TRUNK_HEIGHT, 8]}
          />
          <meshStandardMaterial color={trunkColor} roughness={0.9} />
        </mesh>
      </group>

      <group ref={canopyRef}>
        {foliageClusters.map((c, i) => (
          <mesh key={i} position={[c.x, c.y, c.z]} scale={0}>
            <dodecahedronGeometry args={[c.r, 1]} />
            <meshStandardMaterial color={canopyColor} roughness={0.7} />
          </mesh>
        ))}
      </group>
    </group>
  );
}
